import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Fallback assets
import defaultTourIcon from "../../../assets/tab-tour-bookings.svg";
import defaultPmsIcon from "../../../assets/tab-pms.svg";
import defaultPosIcon from "../../../assets/tab-pos.svg";
import defaultTourImg from "../../../assets/tab-content-tour-bookings.png";
import defaultPmsImg from "../../../assets/tab-content-pms-bookings.png";
import defaultPosImg from "../../../assets/tab-content-pos-bookings.png";

const fallbackDefaults = [
  {
    id: "pms",
    title: "PMS",
    icon: defaultPmsIcon,
    detail:
      "All essential tools consolidated onto a single platform, greatly improving operational efficiency",
    detailList: [
      "Seamless check-in experience, even during peak hours",
      "Powerful reporting with real time analytics for both hotel staff and executives",
      "Easily access reservation information, and efficiently manage guests and payments",
      "Military-grade encryption while providing real-time data access from anywhere",
      "Real-Time Room Status & Task Automation",
    ],
    buttonText: "Get More Info",
    buttonUrl: "/contact-us",
    image: defaultPmsImg,
  },
  {
    id: "pos",
    title: "POS",
    icon: defaultPosIcon,
    detail: "Integrated with Laguna Creek PMS and booking platforms",
    detailList: [
      "Save big on merchant fees",
      "Simple, customer friendly interface",
      "Fast and reliable",
      "Data is secure and safe",
    ],
    buttonText: "Get More Info",
    buttonUrl: "/contact-us",
    image: defaultPosImg,
  },
  {
    id: "booking",
    title: "Booking Platform",
    icon: defaultTourIcon,
    detail:
      "Bundle tour and package sales, and itemize add-ons during purchase",
    detailList: [
      "Industry leading affiliate management portal",
      "Revenue and expenses, permit fee and tax reports, in real time, to track your business",
      "Waivers e-signed, so no need for 3rd party waiver subscriptions",
      "Lightning fast transactions with multiple payment options, including split payments",
    ],
    buttonText: "Get More Info",
    buttonUrl: "https://nativeamericantours.com/",
    image: defaultTourImg,
  },
];

function ProductsTabsSection({ acfData }) {
  const [activeTab, setActiveTab] = useState(0);
  const [fade, setFade] = useState(true);
  const [tabList, setTabList] = useState(fallbackDefaults);
  const [tabContentList, setTabContentList] = useState([]);

  useEffect(() => {
    if (acfData?.pms_pos_booking && acfData.pms_pos_booking.length > 0) {
      const tabs = acfData.pms_pos_booking.map((item, index) => ({
        id: `tab-${index}`,
        title: item.pms_pos_booking_item_title || `Tab ${index + 1}`,
        icon: item.pms_pos_booking_item_title_icon || defaultPmsIcon,
        detail: item.pms_pos_booking_item_detail || "No description available.",
        detailList:
          item.pms_pos_booking_item_detail_list?.map(
            (d) => d.pms_pos_booking_detail_list_item
          ) || [],
        buttonText: item.pms_pos_booking_item_button_text || "Get More Info",
        buttonUrl: item.pms_pos_booking_item_button_url || "#",
        image: item.pms_pos_booking_item_image || defaultPmsImg,
      }));
      setTabList(tabs);
      setActiveTab(tabs[0].id);
    } else {
      setTabList(fallbackDefaults);
      setActiveTab(fallbackDefaults[0].id);
    }
  }, [acfData]);

  useEffect(() => {
    const orderedTabs = [
      ...tabList.filter((item) => item.id === activeTab),
      ...tabList.filter((item) => item.id !== activeTab),
    ];
    setTabContentList(orderedTabs);
  }, [activeTab, tabList]);

  const handleTabChange = (id) => {
    if (id === activeTab) return;
    setFade(false);
    setTimeout(() => {
      setActiveTab(id);
      setFade(true);
    }, 300);
  };
  
  return (
    <section className="pt-4 pb-16">
      <div className="w-full max-w-[1570px] px-4 mx-auto">
        <div className="flex flex-wrap -mx-4 gap-y-[40px] lg:gap-y-[60px]">
          {/* Tabs */}
          <div className="w-full max-w-full px-4">
            <div className="flex justify-center items-center flex-wrap gap-x-[20px] border-b border-[#E8E8E8]">
              {tabList.map((tab, index) => (
                <React.Fragment key={tab.id}>
                  <button
                    className={`text-lg sm:text-2xl lg:text-3xl font-normal !leading-[65px] transition-all duration-300 px-2 sm:px-4 md:px-7 ${
                      tab.id === activeTab
                        ? "text-[#5FA3B2] border-b-[5px] border-[#FAA42A]"
                        : "text-[#192020] hover:text-[#5FA3B2] border-b-[5px] border-transparent"
                    }`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.title}
                  </button>
                  {index !== tabList.length - 1 && (
                    <div className="text-2xl font-light text-[#DDDDDD]">|</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Tab Contents */}
          <div className="w-full max-w-full px-4">
            {tabContentList.map((tab) => {
              const isActive = tab.id === activeTab;
              const animation = isActive
                ? fade
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-2"
                : "";

              return (
                <div
                  key={tab.id}
                  className={`flex flex-wrap items-center -mx-4 mb-10 transition-all duration-300 ease-in-out transform ${animation}`}
                >
                  <div className="w-full lg:w-1/2 max-w-full px-4">
                    <div className="flex flex-col items-start gap-y-[20px] sm:gap-y-[24px] lg:max-w-[600px]">
                      <div className="flex items-center gap-x-5">
                        <img
                          src={tab.icon}
                          alt={tab.title}
                          height="45"
                          width="46"
                        />
                        <h3 className="text-[30px] md:text-[40px] lg:text-[46px] xl:text-[52px] font-normal leading-tight text-[#5fa3b2]">
                          {tab.title}
                        </h3>
                      </div>
                      <p className="text-lg sm:text-2xl font-normal leading-normal text-[#192020]">
                        {tab.detail}
                      </p>
                      <ul className="list-disc pl-8">
                        {tab.detailList.map((item, idx) => (
                          <li
                            key={idx}
                            className="text-lg font-light leading-7 text-[#333333]"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={tab.buttonUrl}
                        target={
                          tab.buttonUrl.startsWith("http") ? "_blank" : "_self"
                        }
                        rel="noopener noreferrer"
                        className="inline-block text-lg font-bold leading-6 text-[#5FA3B2] border border-[#5FA3B2] py-[17px] px-[35px] rounded-xl transition-all hover:text-white hover:bg-[#5FA3B2] mb-8 lg:mb-0"
                      >
                        {tab.buttonText}
                      </Link>
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2 max-w-full px-4">
                    <img src={tab.image} alt={tab.title} className="w-full" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductsTabsSection;
